import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import supabase from "../../lib/supabase";
import { clearAllCaches } from "../../serviceWorkerRegistration";

// Helper function to detect if avatar is from your Supabase storage
const isSupabaseAvatar = (avatarUrl) => {
  if (!avatarUrl) return false;
  
  // Check if it's from your Supabase storage bucket
  return avatarUrl.includes('supabase.co/storage/v1/object/') ||
         avatarUrl.includes('qkvjqrwihdzuyrlcsgsm.supabase.co'); // Your specific instance
};

// Helper function to detect if avatar is from OAuth provider
const isOAuthAvatar = (avatarUrl) => {
  if (!avatarUrl) return false;
  
  return avatarUrl.includes('scontent-') ||           // Facebook/Meta
         avatarUrl.includes('fbcdn.net') ||           // Facebook CDN
         avatarUrl.includes('platform-lookaside.fbsbx.com') || // Facebook
         avatarUrl.includes('lh3.googleusercontent.com') ||     // Google
         avatarUrl.includes('googleusercontent.com') ||         // Google
         avatarUrl.includes('i.scdn.co') ||                     // Spotify
         avatarUrl.includes('mosaic.scdn.co');                  // Spotify
};

// Handle both Universal Links and custom URL schemes
const isCustomSchemeCallback = () => {
  return window.location.protocol === 'musicnet:' || 
         window.location.href.startsWith('musicnet://');
};

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, setIsNewUser, setIsOAuth, syncOneSignalPlayerId } = useAuthStore();

  useEffect(() => {
    const handleCallback = async () => {
      console.log("🔄 Handling auth callback...");

      // IMPORTANT: If we're coming back from OAuth in BuildNatively, prevent going back to error page
      if (window.natively && (window.location.hash.includes('access_token') || 
        window.location.search.includes('code='))) {
        console.log('🔗 [CALLBACK] Processing Universal Link auth callback...');
        console.log('🔗 [CALLBACK] Current URL:', window.location.href);
        console.log('🔗 [CALLBACK] Search params:', location.search);
        console.log('🔗 [CALLBACK] Hash:', location.hash);
        console.log('🔗 [CALLBACK] Pathname:', location.pathname);
        
        // If user tries to go back, push them forward
        const preventBack = (e) => {
          e.preventDefault();
          window.history.forward();
        };
        
        window.addEventListener('popstate', preventBack);
        
        // Clean up the event listener when component unmounts
        const cleanup = () => {
          window.removeEventListener('popstate', preventBack);
        };

        // Also try to close any error pages that might be in the webview history
        if (window.natively && window.natively.closeWebView) {
          try {
            window.natively.closeWebView();
          } catch (e) {
          }
        }

        // Make sure body is visible again (in case it was hidden during OAuth)
        
        // Remove any overlays that might have been added
        const overlays = document.querySelectorAll('[data-oauth-overlay]');
        overlays.forEach(overlay => overlay.remove());
      }

      try {
        console.log('🔗 [CALLBACK] Processing auth callback...', {
          href: window.location.href,
          protocol: window.location.protocol,
          isCustomScheme: isCustomSchemeCallback()
        });
        
        console.log('🔄 [AUTH CALLBACK] Processing OAuth callback...');
        
        // Clear caches to ensure fresh content
        clearAllCaches();
        
        // Send message to service worker to clear cache
        if (navigator.serviceWorker && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'CLEAR_CACHE'
          });
        }

        // Check if this was a specific OAuth provider
        const authProvider = sessionStorage.getItem('authProvider');
        console.log("Auth provider:", authProvider);

        // Check if this is a refresh/navigation vs actual OAuth callback
        const isActualCallback = window.location.hash.includes('access_token') || 
                                window.location.search.includes('code=') ||
                                authProvider; // Only if explicitly set
        
        console.log("Is actual OAuth callback:", isActualCallback);

        // Get session with retries
        let session = null;
        let sessionError = null;
        const maxRetries = 5;
        const delay = 2000;

        for (let i = 0; i < maxRetries; i++) {
          try {
            console.log(`Attempt ${i + 1}: Getting session...`);
            const { data, error } = await supabase.auth.getSession();
            
            if (error) {
              console.error(`Session error on attempt ${i + 1}:`, error);
              sessionError = error;
            } else if (data?.session) {
              console.log(`Session found on attempt ${i + 1}`);
              session = data.session;
              
              // Check if this is an OAuth session
              const isOAuth = !!(
                session.user?.app_metadata?.provider && 
                session.user.app_metadata.provider !== 'email'
              );
              console.log("Is OAuth session:", isOAuth);
              setIsOAuth(isOAuth);
              break;
            }
            
            if (i < maxRetries - 1) {
              console.log(`Retry ${i + 1}: Waiting ${delay}ms for session...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          } catch (err) {
            console.error(`Error on attempt ${i + 1}:`, err);
            sessionError = err;
          }
        }

        if (!session) {
          console.error("❌ No session after all retries:", sessionError);
          return navigate("/auth", { 
            state: { error: "Authentication failed. Please try again." } 
          });
        }

        console.log("✅ Session retrieved");
        
        console.log('🔗 [CALLBACK] ✅ Session exchange successful:', {
          hasSession: !!session,
          userId: session?.user?.id,
          provider: session?.user?.app_metadata?.provider
        });

        // Only refresh token if this is an actual OAuth callback
        if (isActualCallback) {
          console.log("🔄 Refreshing session token...");
          await supabase.auth.refreshSession();
          
          // Wait for profile trigger to complete only for new OAuth flows
          console.log("🔄 Waiting for profile creation...");
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

        // Fetch profile with retries using RPC function
        let profile = null;
        let profileError = null;
        
        for (let i = 0; i < maxRetries; i++) {
          try {
            console.log(`Profile attempt ${i + 1}: Getting profile...`);
            const { data, error } = await supabase
              .rpc('get_current_user_profile');

            if (error) {
              console.error(`Profile error on attempt ${i + 1}:`, error);
              profileError = error;
            } else if (data && data.length > 0) {
              console.log(`Profile found on attempt ${i + 1}`);
              profile = data[0];
              break;
            }
            
            if (i < maxRetries - 1) {
              console.log(`Profile retry ${i + 1}: Waiting ${delay}ms...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          } catch (err) {
            console.error(`Profile fetch error on attempt ${i + 1}:`, err);
            profileError = err;
          }
        }

        if (!profile) {
          console.error("❌ Error fetching profile after all retries:", profileError);
          
          // Only redirect to onboarding if this is an actual OAuth callback
          if (isActualCallback) {
            console.log("Profile not found - redirecting to onboarding to let triggers complete");
            setIsNewUser(true);
            return navigate("/onboarding/name", { replace: true });
          } else {
            // If just a page refresh and no profile, something's wrong - go to auth
            return navigate("/auth", { 
              state: { error: "Please sign in again." } 
            });
          }
        }

        console.log("✅ Profile loaded");
        
        // Handle avatar updates based on your rules - only for actual OAuth callbacks
        if (isActualCallback) {
          const identity = session.user?.identities?.[0];
          const provider = identity?.provider;
          const oauthAvatarUrl = identity?.identity_data?.avatar_url || 
                                 identity?.identity_data?.picture;

          console.log("🖼️ OAuth avatar URL available:", !!oauthAvatarUrl);
          console.log("🏢 Provider:", provider);

          // Your avatar preservation rules:
          if (provider === "spotify") {
            console.log("🎵 Spotify login - checking avatar refresh...");
            
            // Check what type of avatar the user currently has
            const hasSupabaseAvatar = isSupabaseAvatar(profile.avatar_url);
            const hasOAuthAvatar = isOAuthAvatar(profile.avatar_url);
            
            console.log("Has Supabase avatar:", hasSupabaseAvatar);
            console.log("Has OAuth avatar:", hasOAuthAvatar);
            
            // Only update if:
            // 1. User has an OAuth avatar (not custom Supabase one), OR
            // 2. User has no avatar at all
            if (oauthAvatarUrl && !hasSupabaseAvatar) {
              console.log("🔄 Refreshing Spotify avatar URL (user has OAuth or no avatar)...");
              
              try {
                const { data: updatedProfile, error: updateError } = await supabase
                  .from("user_profiles")
                  .update({
                    avatar_url: oauthAvatarUrl,
                    updated_at: new Date().toISOString(),
                  })
                  .eq("id", profile.id)
                  .select()
                  .single();
              
                if (updateError) {
                  console.error("⚠️ Failed to update Spotify avatar_url:", updateError);
                } else {
                  console.log("✅ Spotify avatar_url refreshed successfully");
                  profile.avatar_url = oauthAvatarUrl; // Update local copy
                }
              } catch (err) {
                console.error("❌ Error updating Spotify avatar:", err);
              }
            } else if (hasSupabaseAvatar) {
              console.log("🛡️ User has custom Supabase avatar - preserving it");
            } else {
              console.log("ℹ️ No Spotify avatar refresh needed");
            }
          } else if (provider && oauthAvatarUrl) {
            // For other providers (Google, Facebook, etc.)
            const hasSupabaseAvatar = isSupabaseAvatar(profile.avatar_url);
            
            if (!profile.avatar_url) {
              // User has no avatar - set initial one
              try {
                console.log(`🆕 New ${provider} user - setting initial avatar`);
                const { data: updatedProfile, error: updateError } = await supabase
                  .from("user_profiles")
                  .update({
                    avatar_url: oauthAvatarUrl,
                    updated_at: new Date().toISOString(),
                  })
                  .eq("id", profile.id)
                  .select()
                  .single();
              
                if (updateError) {
                  console.error(`⚠️ Failed to set initial ${provider} avatar:`, updateError);
                } else {
                  console.log(`✅ Initial ${provider} avatar set successfully`);
                  profile.avatar_url = oauthAvatarUrl;
                }
              } catch (err) {
                console.error(`❌ Error setting ${provider} avatar:`, err);
              }
            } else if (hasSupabaseAvatar) {
              console.log("🛡️ User has custom Supabase avatar - preserving it");
            } else {
              console.log(`ℹ️ Keeping existing ${provider} avatar from user_profiles`);
            }
          }
        }

        // 🚨 NEW LOGIC: Determine if this is a new user (sign-up) vs returning user (sign-in)
        const isNewUser = !profile.onboarding_completed || 
                         !profile.full_name || 
                         !profile.display_name;

        console.log('📊 User status in callback:', {
          onboarding_completed: profile.onboarding_completed,
          has_full_name: !!profile.full_name,
          has_display_name: !!profile.display_name,
          isNewUser,
        });

        // 🎯 Set user with notifications ONLY for new users during actual OAuth callbacks
        if (isActualCallback && isNewUser) {
          console.log('🎉 NEW USER detected in OAuth callback - setting up notifications');
          setUser(profile, true); // Will trigger notifications setup in authStore
        } else if (isActualCallback && !isNewUser) {
          console.log('👋 RETURNING USER in OAuth callback - syncing notifications');
          setUser(profile, false); // Will trigger sync in authStore
          // NEW: For returning users, check if their OneSignal ID needs updating
          if (window.NativelyNotifications) {
            setTimeout(() => {
              syncOneSignalPlayerId();
            }, 1000);
          }
        } else {
          console.log('📱 Not an OAuth callback or page refresh');
          setUser(profile); // ❌ PAGE REFRESH/NAVIGATION = no notifications
        }
        
        // Check onboarding status
        const isOnboardingComplete = profile.onboarding_completed && profile.terms_accepted;
        console.log("Onboarding completed:", profile.onboarding_completed);
        console.log("Terms accepted:", profile.terms_accepted);
        console.log("Is onboarding complete:", isOnboardingComplete);
        
        // Check localStorage for additional verification
        const localOnboardingStatus = localStorage.getItem('onboardingStatus');
        console.log("Local onboarding status:", localOnboardingStatus);
        
        if (isOnboardingComplete || localOnboardingStatus === 'complete') {
          // User has completed onboarding
          console.log("User has completed onboarding, redirecting to app...");
          localStorage.setItem('onboardingStatus', 'complete');
          
          // If this is just a page refresh, redirect to current page or home
          const redirectTo = isActualCallback 
            ? (sessionStorage.getItem('authRedirect') || '/') 
            : (window.location.pathname === '/auth/callback' ? '/' : window.location.pathname);
          
          sessionStorage.removeItem('authRedirect');
          console.log("Redirecting completed user to:", redirectTo);
          return navigate(redirectTo, { replace: true });
        } else {
          // User needs to complete onboarding
          setIsNewUser(true);
          
          // Only redirect to onboarding if this is an actual OAuth callback or they're truly incomplete
          if (isActualCallback || !profile.onboarding_completed) {
            console.log("User needs onboarding, redirecting...");
            
            // Determine next onboarding step
            let nextStep = 'name';
            if (profile.full_name && profile.display_name) {
              nextStep = 'profession';
              if (profile.professions?.length > 0) {
                nextStep = 'contact';
                if (profile.location) {
                  nextStep = 'experience';
                  if (profile.experience_level) {
                    nextStep = 'goals';
                    if (profile.goals) {
                      nextStep = 'terms';
                    }
                  }
                }
              }
            }
            
            console.log("Next onboarding step:", nextStep);
            return navigate(`/onboarding/${nextStep}`, { replace: true });
          } else {
            // Determine where to redirect the user
            let redirectPath = '/';
            
            if (!profile.onboarding_completed) {
              console.log('📝 [AUTH CALLBACK] Onboarding incomplete, redirecting to onboarding...');
              redirectPath = `/onboarding/${nextStep}`;
            } else {
              console.log('✅ [AUTH CALLBACK] Onboarding complete, redirecting to main app...');
              redirectPath = sessionStorage.getItem('authRedirect') || '/';
            }
            
            // Use window.location.href to trigger universal link and close the web view
            console.log('🔗 [AUTH CALLBACK] Redirecting to native app:', `https://stridemusiccollective.co.uk${redirectPath}`);
            window.location.href = `https://stridemusiccollective.co.uk${redirectPath}`;
          }
        }
      } catch (error) {
        console.error("🚨 Critical error handling callback:", error);
        
        // Redirect to auth page with error using universal link
        setTimeout(() => {
          window.location.href = 'https://stridemusiccollective.co.uk/auth?error=' + encodeURIComponent(error.message || 'Authentication failed. Please try again.');
        }, 3000);
      }
    };

    handleCallback();
  }, [navigate, setUser, setIsNewUser, setIsOAuth, syncOneSignalPlayerId]);

  // Show loading/error state while processing
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-surface mx-auto mb-4"></div>
        <p className="text-neutral">Processing Universal Link authentication...</p>
      </div>
    </div>
  );
}